import { Observable, from } from 'rxjs';
const IV_SIZE = 12; // Standard IV size for AES-GCM

/**
 * Convert a Base64 encoded string to a Uint8Array
 */
const base64ToBytes = (base64: string): Uint8Array => {
	return new Uint8Array(
		atob(base64)
			.split('')
			.map((char) => char.charCodeAt(0))
	);
};

/**
 * Convert a Uint8Array to a Base64 encoded string
 */
const bytesToBase64 = (bytes: Uint8Array): string => {
	return btoa(String.fromCharCode(...bytes));
};

/**
 * Import AES key from a Base64 string using RxJS
 */
const importAESKey = (base64Key: string): Observable<CryptoKey> => {
	const keyBytes = base64ToBytes(base64Key);
	return from(
		crypto.subtle.importKey(
			'raw',
			keyBytes,
			{ name: 'AES-GCM', length: 256 }, // Explicitly specify key length
			false,
			['encrypt', 'decrypt']
		)
	);
};

/**
 * Encrypt a given text with AES-GCM using RxJS
 */
export const encryptAES = (data: string, base64Key: string): Observable<string> => {
	return new Observable((subscriber) => {
		importAESKey(base64Key).subscribe({
			next: async (key) => {
				try {
					const encoder = new TextEncoder();
					const iv = crypto.getRandomValues(new Uint8Array(IV_SIZE));

					const encryptedData = await crypto.subtle.encrypt(
						{ name: 'AES-GCM', iv },
						key,
						encoder.encode(data)
					);

					// Combine IV + encrypted data and return as Base64
					const combined = new Uint8Array(IV_SIZE + encryptedData.byteLength);
					combined.set(iv, 0);
					combined.set(new Uint8Array(encryptedData), IV_SIZE);

					subscriber.next(bytesToBase64(combined));
					subscriber.complete();
				} catch (error) {
					subscriber.error(error);
				}
			},
			error: (err) => subscriber.error(err),
		});
	});
};
